import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, Calendar, MapPin, TrendingUp, Zap, Target, User } from 'lucide-react';
import type { Player } from '../../types/player';
import { translatePosition, translateFoot } from '../../utils/positionTranslations';

interface PlayerCardProps {
  player: Player;
  onViewProfile?: (playerId: string | number) => void;
}

const PlayerCard: React.FC<PlayerCardProps> = ({ player, onViewProfile }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'es' | 'en';

  // Helper to get player name from either old or new structure
  // Removes "(id)" format from player names
  const getPlayerName = () => {
    const name = player.player_name || player.nombre_transfermarkt || t('players.noName');
    // Remove "(id)" format - extract only the name before the opening parenthesis
    const cleanName = name.split('(')[0].trim();
    return cleanName || name; // fallback to original if empty
  };

  // Helper to get position from either old or new structure
  const getPosition = () => {
    const position = player.position || player.posicion_transfermarkt || 'N/A';
    return translatePosition(position, currentLang);
  };

  // Helper to get birth date from either old or new structure
  const getBirthDate = () => {
    return player.date_of_birth || player.fecha_nacimiento_transfermarkt;
  };

  // Helper to get club name from either old or new structure
  const getClubName = () => {
    return player.current_club_name || player.equipo_transfermarkt || t('players.noTeam');
  };

  // Helper to get contract expiry from either old or new structure
  const getContractExpiry = () => {
    return player.contract_expires || player.fecha_fin_contrato;
  };

  // Helper to get image URL from either old or new structure
  const getImageUrl = () => {
    return player.player_image_url || player.url_foto_jugador;
  };

  // Helper to get nationality
  const getNationality = () => {
    return player.citizenship || player.nacion || player.place_of_birth_country;
  };

  const getAge = (birthDate?: string) => {
    if (!birthDate) return 'N/A';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    }
    return `€${(amount / 1000).toFixed(0)}K`;
  };

  const formatContractEnd = (contractEnd?: string) => {
    if (!contractEnd) return t('players.notAvailable');
    const date = new Date(contractEnd);
    const today = new Date();
    const monthsLeft = Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30));
    
    if (monthsLeft < 0) return 'Expirado';
    if (monthsLeft < 6) return `${monthsLeft}m`;
    
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short'
    });
  };

  // Get position color
  const getPositionColor = () => {
    // Use consistent primary blue for all positions for clean, professional look
    return 'bg-[#0F2A7D]';
  };

  const handleViewProfile = (e?: React.MouseEvent) => {
    // Prevent any default behavior or event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Usar el ID disponible, priorizando player_id (nuevo), luego id_fbref, luego id_transfermarkt
    const playerId = player.player_id || player.id_fbref || player.id_transfermarkt?.toString();
    
    if (!playerId) {
      console.error('No se encontró ID válido para el jugador:', player);
      return;
    }

    // Always use the callback if provided, otherwise use direct navigation
    if (onViewProfile) {
      onViewProfile(playerId);
    } else {
      // Direct navigation as fallback
      navigate(`/sporting/player-detail/${playerId}`);
    }
  };

  return (
    <div className="group relative bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 border border-gray-100 overflow-hidden h-full flex flex-col">
      {/* Subtle hover overlay */}
      <div className="absolute inset-0 bg-[#0F2A7D]/0 group-hover:bg-[#0F2A7D]/3 transition-all duration-300 pointer-events-none z-10"></div>
      
      {/* Compact header section */}
      <div className="relative bg-[#0F2A7D] px-4 pt-4 pb-14">
        {/* Position and Value badges - Compact */}
        <div className="flex justify-between items-start mb-3">
          <div className={`${getPositionColor()} text-white text-[10px] font-bold px-2 py-1 rounded-md border border-white/10`}>
            {getPosition()}
          </div>
          {player.valor_mercado && (
            <div className="bg-white/20 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-md border border-white/20">
              {formatCurrency(player.valor_mercado)}
            </div>
          )}
        </div>

        {/* Compact circular photo */}
        <div className="flex justify-center">
          <div className="relative w-16 h-16 rounded-full overflow-hidden bg-white/90 border-2 border-white/40 shadow-md group-hover:scale-105 transition-transform duration-300">
            {getImageUrl() ? (
              <img 
                src={getImageUrl()} 
                alt={getPlayerName()}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                <User className="w-8 h-8 text-gray-400" />
              </div>
            )}
          </div>
        </div>

        {/* Compact player name and age */}
        <div className="text-center mt-2.5">
          <h3 className="text-sm font-bold text-white drop-shadow-md line-clamp-1 mb-0.5">
            {getPlayerName()}
          </h3>
          <div className="flex items-center justify-center space-x-1.5 text-white/90 text-xs">
            <Calendar className="w-3 h-3" />
            <span className="font-semibold">{getAge(getBirthDate())} {t('players.years')}</span>
            {getNationality() && (
              <>
                <span className="text-white/60">•</span>
                <span className="font-medium truncate max-w-[80px]">{getNationality()}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content section - Compact */}
      <div className="p-3.5 space-y-2.5 flex-1 flex flex-col -mt-8 relative z-20">
        {/* Club info card - Compact */}
        <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
          <div className="flex items-center space-x-2.5">
            {player.has_enriched_data && player.url_foto_equipo && (
              <div className="flex-shrink-0">
                <img 
                  src={player.url_foto_equipo} 
                  alt={getClubName()}
                  className="w-9 h-9 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
            {!player.has_enriched_data && (
              <div className="flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#0F2A7D]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">{getClubName()}</p>
              {player.competicion && (
                <p className="text-xs text-gray-600 truncate">{player.competicion}</p>
              )}
            </div>
          </div>
        </div>

        {/* Compact KPI Stats */}
        <div className="grid grid-cols-2 gap-2">
          {(player.goles !== undefined && player.goles !== null) && (
            <div className="bg-gray-50 rounded-lg p-2 border border-gray-100 hover:border-[#0F2A7D] hover:shadow-sm transition-all">
              <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide flex items-center gap-1">
                <Target className="w-3 h-3 text-green-600" />
                <span>{t('players.goals')}</span>
              </div>
              <div className="text-xl font-black text-gray-900">
                {player.goles}
              </div>
            </div>
          )}

          {(player.asistencias !== undefined && player.asistencias !== null) && (
            <div className="bg-gray-50 rounded-lg p-2 border border-gray-100 hover:border-[#0F2A7D] hover:shadow-sm transition-all">
              <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide flex items-center gap-1">
                <Zap className="w-3 h-3 text-[#0F2A7D]" />
                <span>{t('players.assistsShort')}</span>
              </div>
              <div className="text-xl font-black text-gray-900">
                {player.asistencias}
              </div>
            </div>
          )}

          {getContractExpiry() && (
            <div className="bg-gray-50 rounded-lg p-2 border border-gray-100 hover:border-[#0F2A7D] hover:shadow-sm transition-all">
              <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{t('players.contract')}</span>
              </div>
              <div className="text-xs font-black text-gray-900">
                {formatContractEnd(getContractExpiry())}
              </div>
            </div>
          )}

          {(player.altura || player.height) && (
            <div className="bg-gray-50 rounded-lg p-2 border border-gray-100 hover:border-[#0F2A7D] hover:shadow-sm transition-all">
              <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide">{t('players.height')}</div>
              <div className="text-xs font-black text-gray-900">
                {player.height || `${player.altura} cm`}
              </div>
            </div>
          )}

          {player.foot && (
            <div className="bg-gray-50 rounded-lg p-2 border border-gray-100 hover:border-[#0F2A7D] hover:shadow-sm transition-all">
              <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide">{t('players.foot')}</div>
              <div className="text-xs font-black text-gray-900 capitalize">
                {translateFoot(player.foot, currentLang)}
              </div>
            </div>
          )}

          {(player.minutos_jugados !== undefined && player.minutos_jugados !== null) && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-[#0F2A7D] hover:shadow-sm transition-all">
              <div className="text-[11px] font-semibold text-gray-600 mb-1 uppercase tracking-wide flex items-center gap-1">
                <TrendingUp className="w-3.5 h-3.5" />
                <span>Min</span>
              </div>
              <div className="text-base font-black text-gray-900">
                {player.minutos_jugados.toLocaleString()}
              </div>
            </div>
          )}

          {/* Additional stats if available - Compact */}
          {(player.partidos_jugados !== undefined && player.partidos_jugados !== null) && (
            <div className="bg-gray-50 rounded-lg p-2 border border-gray-100 hover:border-[#0F2A7D] hover:shadow-sm transition-all">
              <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide">Partidos</div>
              <div className="text-xs font-black text-gray-900">
                {player.partidos_jugados}
              </div>
            </div>
          )}

          {(player.tarjetas_amarillas !== undefined && player.tarjetas_amarillas !== null && player.tarjetas_amarillas > 0) && (
            <div className="bg-gray-50 rounded-lg p-2 border border-gray-100 hover:border-[#0F2A7D] hover:shadow-sm transition-all">
              <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide">{t('players.yellowCards')}</div>
              <div className="text-xs font-black text-yellow-600">
                {player.tarjetas_amarillas}
                {(player.tarjetas_rojas !== undefined && player.tarjetas_rojas !== null && player.tarjetas_rojas > 0) && (
                  <span className="text-red-600 ml-1">/ {player.tarjetas_rojas}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Compact View Profile Button */}
        <button
          onClick={handleViewProfile}
          type="button"
          className="w-full mt-auto bg-[#0F2A7D] hover:bg-[#0A1F5C] text-white font-bold py-2.5 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md transform hover:scale-[1.01] active:scale-[0.99]"
        >
          <Eye className="w-4 h-4" />
          <span className="text-xs uppercase tracking-wide">{t('players.viewProfile')}</span>
        </button>
      </div>
    </div>
  );
};

export default PlayerCard;